import React, { useState, useEffect, useCallback } from 'react';
import { Download, Share2, MessageCircle, Mail, Twitter, Facebook, Linkedin, Copy, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';

interface DocumentActionButtonsProps {
  variant?: 'light' | 'dark' | 'gradient';
  pageTitle?: string;
  onDownload?: () => void;
}

export const DocumentActionButtons: React.FC<DocumentActionButtonsProps> = ({
  variant = 'light',
  pageTitle = 'Growth Energy Documentation',
  onDownload,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    toast.info('Preparing your PDF...');
    
    if (onDownload) {
      onDownload();
    } else {
      window.print();
    }
    
    setTimeout(() => {
      setIsDownloading(false);
      toast.success('Document ready for download!');
    }, 1000);
  }, [onDownload]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform: string) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`Check out ${pageTitle}`);
    
    const shareUrls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      email: `mailto:?subject=${text}&body=${url}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
      toast.success(`Opening ${platform} share dialog...`);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleDownload();
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [handleDownload]);

  // Variant-based styles
  const buttonVariant = variant === 'gradient' ? 'secondary' : variant === 'dark' ? 'outline' : 'outline';
  const buttonClass = variant === 'gradient' 
    ? 'bg-background/10 backdrop-blur-sm hover:bg-background/20 text-primary-foreground border-white/20' 
    : '';

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 no-print">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={buttonVariant}
              size="lg"
              onClick={handleDownload}
              disabled={isDownloading}
              className={`gap-2 min-w-[140px] ${buttonClass}`}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5" />
                  Download PDF
                </>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Download as PDF (Ctrl/Cmd + S)</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={buttonVariant}
                  size="lg"
                  className={`gap-2 min-w-[120px] ${buttonClass}`}
                >
                  <Share2 className="h-5 w-5" />
                  Share
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Share this page</p>
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={handleCopyLink} className="gap-3 py-3 cursor-pointer">
              {copied ? (
                <>
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="font-medium">Link Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="h-5 w-5" />
                  <span>Copy Link</span>
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleShare('whatsapp')} className="gap-3 py-3 cursor-pointer">
              <MessageCircle className="h-5 w-5 text-green-600" />
              <span>WhatsApp</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare('email')} className="gap-3 py-3 cursor-pointer">
              <Mail className="h-5 w-5 text-blue-600" />
              <span>Email</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare('twitter')} className="gap-3 py-3 cursor-pointer">
              <Twitter className="h-5 w-5 text-sky-500" />
              <span>Twitter / X</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare('facebook')} className="gap-3 py-3 cursor-pointer">
              <Facebook className="h-5 w-5 text-blue-700" />
              <span>Facebook</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare('linkedin')} className="gap-3 py-3 cursor-pointer">
              <Linkedin className="h-5 w-5 text-blue-800" />
              <span>LinkedIn</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  );
};
